<!-- Wave-R B 端评价管理 -->
<template>
  <div>
    <el-card shadow="never">
      <el-tabs v-model="activeTab" class="mb-16px" @tab-change="handleTabChange">
        <el-tab-pane label="全部" name="ALL" />
        <el-tab-pane label="差评" name="BAD" />
        <el-tab-pane label="未回复" name="UNREPLIED" />
      </el-tabs>

      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="72px">
        <el-form-item label="订单 ID" prop="orderId">
          <el-input
            v-model="queryParams.orderId"
            class="!w-160px"
            clearable
            placeholder="订单 ID"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="师傅" prop="providerId">
          <el-select v-model="queryParams.providerId" class="!w-180px" clearable filterable placeholder="全部师傅">
            <el-option v-for="p in providerOptions" :key="p.id" :label="p.realName" :value="p.id" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column align="center" label="ID" prop="id" width="72" />
        <el-table-column align="center" label="用户" prop="userNickname" width="100" />
        <el-table-column align="center" label="评分" prop="overallScore" width="100">
          <template #default="{ row }">
            <el-rate :model-value="Number(row.overallScore)" disabled show-score text-color="#ff9900" />
          </template>
        </el-table-column>
        <el-table-column align="center" label="服务" min-width="120" prop="serviceName" />
        <el-table-column align="center" label="师傅" prop="providerName" width="100" />
        <el-table-column label="评价内容" min-width="180" prop="content" show-overflow-tooltip />
        <el-table-column align="center" label="回复" width="80">
          <template #default="{ row }">
            <el-tag :type="row.replied ? 'success' : 'warning'" size="small">
              {{ row.replied ? '已回复' : '未回复' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" label="评价时间" prop="createTime" width="170" />
        <el-table-column align="center" fixed="right" label="操作" width="140">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
            <el-button link type="success" @click="openReply(row)">回复</el-button>
          </template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-dialog v-model="detailVisible" title="评价详情" width="640px">
      <el-descriptions v-if="detail" :column="2" border>
        <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="综合评分">{{ detail.overallScore }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ detail.userNickname }}</el-descriptions-item>
        <el-descriptions-item label="师傅">{{ detail.providerName }}</el-descriptions-item>
        <el-descriptions-item label="服务" :span="2">{{ detail.serviceName }}</el-descriptions-item>
        <el-descriptions-item label="评价内容" :span="2">{{ detail.content || '—' }}</el-descriptions-item>
        <el-descriptions-item label="机构回复" :span="2">
          <div v-if="detail.replies?.length" class="reply-timeline">
            <div v-for="(item, idx) in detail.replies" :key="idx" class="reply-item">
              <div class="reply-meta">{{ formatReplyTime(item.replyTime) }} · {{ replyInstitutionName(item) }}</div>
              <div>{{ item.content }}</div>
            </div>
          </div>
          <span v-else>{{ detail.replyContent || '（未回复）' }}</span>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button type="success" @click="openReply(detail!)">追加回复</el-button>
        <el-button @click="detailVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="replyVisible" :title="`回复评价 · ${currentInstitutionName}`" width="520px">
      <el-form ref="replyFormRef" :model="replyForm" :rules="replyRules" label-width="80px">
        <el-form-item label="回复内容" prop="replyContent">
          <el-input
            v-model="replyForm.replyContent"
            :maxlength="500"
            :placeholder="`请输入${currentInstitutionName}的回复（500字内）`"
            show-word-limit
            type="textarea"
            :rows="4"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="replyVisible = false">取消</el-button>
        <el-button type="primary" @click="submitReply">提交回复</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import { getO2oInstitutionContext } from '@/api/merchant/o2o/institution'
import { getO2oMerchantProviderPage } from '@/api/merchant/o2o/provider'
import {
  getO2oMerchantReview,
  getO2oMerchantReviewPage,
  replyO2oMerchantReview,
  type O2oReviewMerchantVO
} from '@/api/merchant/o2o/review'

defineOptions({ name: 'MerchantO2oReview' })

const message = useMessage()

const activeTab = ref('ALL')
const loading = ref(false)
const list = ref<O2oReviewMerchantVO[]>([])
const total = ref(0)
const providerOptions = ref<{ id: number; realName: string }[]>([])
const myInstitutionName = ref('')

const currentInstitutionName = computed(
  () => detail.value?.institutionName || myInstitutionName.value || '本机构'
)

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  orderId: undefined as string | undefined,
  providerId: undefined as number | undefined
})
const queryFormRef = ref<FormInstance>()

const detailVisible = ref(false)
const detail = ref<O2oReviewMerchantVO | null>(null)

const replyVisible = ref(false)
const replyFormRef = ref<FormInstance>()
const replyForm = reactive({ id: 0, replyContent: '' })
const replyRules: FormRules = {
  replyContent: [{ required: true, message: '请输入回复内容', trigger: 'blur' }]
}

const loadProviders = async () => {
  try {
    const data = await getO2oMerchantProviderPage({ pageNo: 1, pageSize: 100 })
    providerOptions.value = (data.list || []).map((p: any) => ({ id: p.id, realName: p.realName || `师傅#${p.id}` }))
  } catch {
    providerOptions.value = []
  }
}

const getList = async () => {
  loading.value = true
  try {
    const params: any = {
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      tab: activeTab.value,
      providerId: queryParams.providerId
    }
    if (queryParams.orderId) params.orderId = Number(queryParams.orderId)
    const data = await getO2oMerchantReviewPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleTabChange = () => {
  queryParams.pageNo = 1
  getList()
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openDetail = async (id: number) => {
  detail.value = await getO2oMerchantReview(id)
  detailVisible.value = true
}

const openReply = (row: O2oReviewMerchantVO) => {
  replyForm.id = row.id
  replyForm.replyContent = ''
  replyVisible.value = true
}

const formatReplyTime = (t?: string) => (t ? t.replace('T', ' ').slice(0, 16) : '—')

const replyInstitutionName = (item: { institutionName?: string }) =>
  item.institutionName || detail.value?.institutionName || myInstitutionName.value || '本机构'

const loadInstitutionContext = async () => {
  try {
    const ctx = await getO2oInstitutionContext()
    myInstitutionName.value = ctx?.institutionName || ''
  } catch {
    myInstitutionName.value = ''
  }
}

const submitReply = async () => {
  await replyFormRef.value?.validate()
  await replyO2oMerchantReview({ id: replyForm.id, replyContent: replyForm.replyContent.trim() })
  message.success('回复成功')
  replyVisible.value = false
  if (detailVisible.value && detail.value?.id === replyForm.id) {
    detail.value = await getO2oMerchantReview(replyForm.id)
  }
  await getList()
}

onMounted(async () => {
  await loadInstitutionContext()
  await loadProviders()
  await getList()
})
</script>

<style scoped>
.reply-timeline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.reply-item {
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
}
.reply-meta {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
</style>
